import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
    FileText, ShoppingCart, Package, CreditCard,
    TrendingUp, TrendingDown, Clock, AlertCircle,
    ArrowRight, Calendar
} from 'lucide-react';
import { PageHeader } from '../components/layout';
import { Card, CardHeader, CardBody, Badge, Skeleton } from '../components/common';
import { dashboardApi } from '../services/api';
import useAuthStore from '../stores/authStore';
import './Dashboard.css';

export default function Dashboard() {
    const navigate = useNavigate();
    const { user } = useAuthStore();
    const [loading, setLoading] = useState(true);
    const [data, setData] = useState({
        overview: null,
        pendingActions: [],
        monthlyTrend: []
    });

    useEffect(() => {
        fetchDashboardData();
    }, []);

    const fetchDashboardData = async () => {
        try {
            setLoading(true);
            const [overview, pending, trend] = await Promise.all([
                dashboardApi.getOverview(),
                dashboardApi.getPendingActions(),
                dashboardApi.getMonthlyTrend()
            ]);

            // Convert pending actions object to array format
            const pendingData = pending.data?.data || pending.data || {};
            const pendingActionsArray = [];

            if (pendingData.pendingPRApprovals > 0) {
                pendingActionsArray.push({
                    id: 'pr_approval',
                    type: 'pr_approval',
                    title: 'Purchase Requests',
                    description: `${pendingData.pendingPRApprovals} pending approval`,
                    amount: pendingData.pendingPRApprovals,
                    priority: 'high'
                });
            }
            if (pendingData.pendingPOApprovals > 0) {
                pendingActionsArray.push({
                    id: 'po_approval',
                    type: 'po_approval',
                    title: 'Purchase Orders',
                    description: `${pendingData.pendingPOApprovals} pending approval`,
                    amount: pendingData.pendingPOApprovals,
                    priority: 'high'
                });
            }
            if (pendingData.pendingInvoiceVerification > 0) {
                pendingActionsArray.push({
                    id: 'invoice_verify',
                    type: 'invoice_verify',
                    title: 'Invoice Verification',
                    description: `${pendingData.pendingInvoiceVerification} pending verification`,
                    amount: pendingData.pendingInvoiceVerification,
                    priority: 'medium'
                });
            }
            if (pendingData.pendingInvoiceApproval > 0) {
                pendingActionsArray.push({
                    id: 'invoice_approve',
                    type: 'invoice_approve',
                    title: 'Invoice Approval',
                    description: `${pendingData.pendingInvoiceApproval} pending approval`,
                    amount: pendingData.pendingInvoiceApproval,
                    priority: 'medium'
                });
            }
            if (pendingData.pendingPayments > 0) {
                pendingActionsArray.push({
                    id: 'payment',
                    type: 'payment',
                    title: 'Pending Payments',
                    description: `${pendingData.pendingPayments} payments pending`,
                    amount: pendingData.pendingPayments,
                    priority: 'normal'
                });
            }
            if (pendingData.expiringContracts > 0) {
                pendingActionsArray.push({
                    id: 'contract_expiring',
                    type: 'contract_expiring',
                    title: 'Expiring Contracts',
                    description: `${pendingData.expiringContracts} contracts expiring soon`,
                    amount: pendingData.expiringContracts,
                    priority: 'high'
                });
            }
            if (pendingData.overdueInvoices > 0) {
                pendingActionsArray.push({
                    id: 'invoice_overdue',
                    type: 'invoice_overdue',
                    title: 'Overdue Invoices',
                    description: `${pendingData.overdueInvoices} invoices overdue`,
                    amount: pendingData.overdueInvoices,
                    priority: 'high'
                });
            }

            setData({
                overview: overview.data?.data || overview.data,
                pendingActions: pendingActionsArray,
                monthlyTrend: trend.data?.data || trend.data || []
            });
        } catch (error) {
            console.error('Failed to fetch dashboard data:', error);
            // Use mock data for demo
            setData({
                overview: {
                    totalPR: 156,
                    totalPO: 89,
                    totalGR: 67,
                    totalPayments: 45,
                    pendingApprovals: 12,
                    monthlySpending: 2500000000,
                    budgetUtilization: 68.5
                },
                pendingActions: [
                    { id: 1, type: 'pr_approval', title: 'PR-IT-202412-0023', description: 'Office Equipment Request', amount: 15000000, date: '2024-12-20', priority: 'high' },
                    { id: 2, type: 'po_approval', title: 'PO-202412-00015', description: 'IT Infrastructure', amount: 85000000, date: '2024-12-19', priority: 'medium' },
                    { id: 3, type: 'invoice_verify', title: 'INV-2024-0089', description: 'PT Vendor ABC', amount: 25000000, date: '2024-12-18', priority: 'normal' }
                ],
                monthlyTrend: [
                    { month: 'Jul', amount: 1200000000 },
                    { month: 'Aug', amount: 1450000000 },
                    { month: 'Sep', amount: 1100000000 },
                    { month: 'Oct', amount: 1800000000 },
                    { month: 'Nov', amount: 2100000000 },
                    { month: 'Dec', amount: 2500000000 }
                ]
            });
        } finally {
            setLoading(false);
        }
    };

    const formatCurrency = (value) => {
        return new Intl.NumberFormat('id-ID', {
            style: 'currency',
            currency: 'IDR',
            minimumFractionDigits: 0,
            maximumFractionDigits: 0
        }).format(value);
    };

    const formatShortCurrency = (value) => {
        if (value >= 1000000000) {
            return `Rp ${(value / 1000000000).toFixed(1)}B`;
        }
        if (value >= 1000000) {
            return `Rp ${(value / 1000000).toFixed(1)}M`;
        }
        return formatCurrency(value);
    };

    const kpiCards = [
        {
            key: 'pr',
            label: 'Purchase Requests',
            value: data.overview?.counts?.purchaseRequests || data.overview?.totalPR || 0,
            icon: FileText,
            color: 'blue',
            trend: '+12%',
            trendUp: true,
            link: '/purchase-requests'
        },
        {
            key: 'po',
            label: 'Purchase Orders',
            value: data.overview?.counts?.purchaseOrders || data.overview?.totalPO || 0,
            icon: ShoppingCart,
            color: 'green',
            trend: '+8%',
            trendUp: true,
            link: '/purchase-orders'
        },
        {
            key: 'gr',
            label: 'Goods Receipts',
            value: data.overview?.counts?.goodsReceipts || data.overview?.totalGR || 0,
            icon: Package,
            color: 'purple',
            trend: '-3%',
            trendUp: false,
            link: '/goods-receipts'
        },
        {
            key: 'invoices',
            label: 'Invoices',
            value: data.overview?.counts?.invoices || data.overview?.totalPayments || 0,
            icon: CreditCard,
            color: 'orange',
            trend: '+5%',
            trendUp: true,
            link: '/invoices'
        }
    ];

    const getActionIcon = (type) => {
        switch (type) {
            case 'pr_approval': return FileText;
            case 'po_approval': return ShoppingCart;
            case 'invoice_verify':
            case 'invoice_approve':
            case 'invoice_overdue': return CreditCard;
            case 'payment': return CreditCard;
            case 'contract_expiring': return Calendar;
            default: return AlertCircle;
        }
    };

    const getActionLabel = (type) => {
        switch (type) {
            case 'pr_approval': return 'PR Approval';
            case 'po_approval': return 'PO Approval';
            case 'invoice_verify': return 'Invoice Verification';
            case 'invoice_approve': return 'Invoice Approval';
            case 'invoice_overdue': return 'Overdue Invoices';
            case 'payment': return 'Pending Payments';
            case 'contract_expiring': return 'Expiring Contracts';
            default: return 'Action Required';
        }
    };

    const getPriorityVariant = (priority) => {
        switch (priority) {
            case 'high': return 'danger';
            case 'medium': return 'warning';
            default: return 'default';
        }
    };

    return (
        <div className="dashboard">
            <PageHeader
                title={`Welcome back, ${user?.fullName?.split(' ')[0] || 'User'}!`}
                subtitle="Here's what's happening with your procurement today."
            />

            {/* KPI Cards */}
            <div className="dashboard-kpi-grid">
                {kpiCards.map((kpi) => (
                    <Card key={kpi.key} className={`kpi-card kpi-${kpi.color}`} hoverable onClick={() => navigate(kpi.link)}>
                        <CardBody>
                            <div className="kpi-header">
                                <div className={`kpi-icon kpi-icon-${kpi.color}`}>
                                    <kpi.icon size={24} />
                                </div>
                                <div className={`kpi-trend ${kpi.trendUp ? 'up' : 'down'}`}>
                                    {kpi.trendUp ? <TrendingUp size={14} /> : <TrendingDown size={14} />}
                                    {kpi.trend}
                                </div>
                            </div>
                            {loading ? (
                                <Skeleton height={36} width={60} />
                            ) : (
                                <div className="kpi-value">{kpi.value}</div>
                            )}
                            <div className="kpi-label">{kpi.label}</div>
                        </CardBody>
                    </Card>
                ))}
            </div>

            {/* Main Content Grid */}
            <div className="dashboard-content-grid">
                {/* Pending Actions */}
                <Card className="pending-actions-card">
                    <CardHeader
                        action={
                            <button className="view-all-btn" onClick={() => navigate('/notifications')}>
                                View All <ArrowRight size={14} />
                            </button>
                        }
                    >
                        <h3>Pending Actions</h3>
                        <p>Items requiring your attention</p>
                    </CardHeader>
                    <CardBody>
                        {loading ? (
                            <div className="pending-list">
                                {[1, 2, 3].map(i => (
                                    <div key={i} className="pending-item">
                                        <Skeleton variant="circular" width={40} height={40} />
                                        <div style={{ flex: 1 }}>
                                            <Skeleton width="60%" height={16} />
                                            <Skeleton width="40%" height={14} />
                                        </div>
                                    </div>
                                ))}
                            </div>
                        ) : data.pendingActions.length === 0 ? (
                            <div className="pending-empty">
                                <Clock size={48} />
                                <p>No pending actions</p>
                            </div>
                        ) : (
                            <div className="pending-list">
                                {data.pendingActions.map((action) => {
                                    const Icon = getActionIcon(action.type);
                                    return (
                                        <div key={action.id} className="pending-item">
                                            <div className="pending-icon">
                                                <Icon size={20} />
                                            </div>
                                            <div className="pending-content">
                                                <div className="pending-title">
                                                    <span>{action.title}</span>
                                                    <Badge variant={getPriorityVariant(action.priority)} size="sm">
                                                        {action.priority}
                                                    </Badge>
                                                </div>
                                                <div className="pending-meta">
                                                    <span>{action.description}</span>
                                                </div>
                                            </div>
                                        </div>
                                    );
                                })}
                            </div>
                        )}
                    </CardBody>
                </Card>

                {/* Monthly Spending Trend */}
                <Card className="trend-card">
                    <CardHeader
                        action={
                            <div className="trend-period">
                                <Calendar size={14} />
                                <span>Last 6 months</span>
                            </div>
                        }
                    >
                        <h3>Monthly Spending</h3>
                        <p>Spending trend over time</p>
                    </CardHeader>
                    <CardBody>
                        {loading ? (
                            <Skeleton height={200} />
                        ) : (
                            <div className="trend-chart">
                                <div className="chart-bars">
                                    {data.monthlyTrend.map((item, index) => {
                                        const maxAmount = Math.max(...data.monthlyTrend.map(t => t.amount));
                                        const height = (item.amount / maxAmount) * 100;
                                        return (
                                            <div key={index} className="chart-bar-container">
                                                <div
                                                    className="chart-bar"
                                                    style={{ height: `${height}%` }}
                                                >
                                                    <span className="chart-tooltip">{formatShortCurrency(item.amount)}</span>
                                                </div>
                                                <span className="chart-label">{item.month}</span>
                                            </div>
                                        );
                                    })}
                                </div>
                            </div>
                        )}
                    </CardBody>
                </Card>

                {/* Quick Stats */}
                <Card className="quick-stats-card">
                    <CardHeader>
                        <h3>Quick Stats</h3>
                        <p>Key metrics overview</p>
                    </CardHeader>
                    <CardBody>
                        <div className="quick-stats">
                            <div className="quick-stat">
                                <div className="stat-label">Pending Approvals</div>
                                {loading ? (
                                    <Skeleton width={40} height={28} />
                                ) : (
                                    <div className="stat-value text-warning">{data.overview?.pendingApprovals || 0}</div>
                                )}
                            </div>
                            <div className="quick-stat">
                                <div className="stat-label">Monthly Spending</div>
                                {loading ? (
                                    <Skeleton width={80} height={28} />
                                ) : (
                                    <div className="stat-value">{formatShortCurrency(data.overview?.monthlySpending || 0)}</div>
                                )}
                            </div>
                            <div className="quick-stat">
                                <div className="stat-label">Budget Utilization</div>
                                {loading ? (
                                    <Skeleton width={60} height={28} />
                                ) : (
                                    <>
                                        <div className="stat-value">{data.overview?.budgetUtilization || 0}%</div>
                                        <div className="progress-bar">
                                            <div
                                                className="progress-fill"
                                                style={{ width: `${data.overview?.budgetUtilization || 0}%` }}
                                            />
                                        </div>
                                    </>
                                )}
                            </div>
                        </div>
                    </CardBody>
                </Card>
            </div>
        </div>
    );
}
